/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import MuiAlertTitle from '@mui/material/AlertTitle';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';
import { Copy } from 'src/common/ui/components/Copy';

const ERROR_FOLLOW_UP_MESSAGE =
  'Please file a helpdesk ticket and include a screenshot of this error (if no PHI is in the screenshot).';

const getBackgroundColor = (severity: AlertProps['severity']) => {
  switch (severity) {
    case 'error':
      return Colors.Red1;
    case 'warning':
      return Colors.Yellow1;
    case 'info':
      return Colors.Mist;
    case 'success':
      return Colors.Green1;
    default:
      return 'transparent';
  }
};

export const Alert = ({
  error,
  message,
  severity = 'error',
  title,
}: {
  message?: string;
  error?: any;
  title?: string;
} & Pick<AlertProps, 'severity'>) => {
  const errorDetails = error?.message || JSON.stringify(error, null, 2);

  return (
    <MuiAlert
      css={css`
        padding: ${Spacing.Unit2}px ${Spacing.Unit3}px;
        margin-bottom: ${Spacing.Unit3}px;
        background-color: ${getBackgroundColor(severity)};
      `}
      data-testid="nds-components-alert"
      severity={severity}
    >
      {title && <MuiAlertTitle css={TypographyStyles.subtitle1}>{title}</MuiAlertTitle>}

      {message && (
        <Copy
          css={css`
            color: ${Colors.CoolGray9};
          `}
        >
          {message}
        </Copy>
      )}

      {severity === 'error' && (
        <Copy
          css={css`
            color: ${Colors.CoolGray9};
          `}
        >
          {ERROR_FOLLOW_UP_MESSAGE}
        </Copy>
      )}

      {errorDetails && (
        <pre
          css={css`
            color: ${Colors.CoolGray9};
            margin: 0;
            margin-top: ${Spacing.Unit2}px;
            white-space: pre-wrap;
          `}
        >
          <code>{errorDetails}</code>
        </pre>
      )}
    </MuiAlert>
  );
};
